import React, { useState, Fragment } from 'react';
import theme from './ProjectDashboard.module.scss';
import { Trans } from '@lingui/react/macro';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import { useAppSelector, useAsyncState } from '@/utils/hooks';
import {
    getProjectDashboardSpending,
    getProjectDashboardAvailability,
    ProjectSpendingRow,
    getProjectDashboardTimeToOffer,
    getProjectDashboardDeadlineOverview,
    getProjectChangeRequestStatusOverview,
} from '@/services/project-dashboard';
import { ProjectRelativeLink } from '@/components/navigation';
import { findCategoryName } from '@/utils/categories';
import { SellerChangeRequestStatus } from '@/types';
import ChangeRequestStatusIndicator from '@/components/StatusIndicator/ChangeRequestSellerStatusIndicator';
import { ResidentialUnitFilterOptions } from '../residential-units/ResidentialUnitsFilter';
import { getFilterQueryString } from '@/utils/url';
import { ChangeRequestFilterValue } from '../change-requests/ChangeRequestFilter';
import { DashboardCardGrid, DashboardDataCard, SimpleBarChart } from '@ygtechnologies/bygr-frontend-component-library';
import { ProjectCurrencyDisplay } from '../currency';

interface ProjectDashboardProps {
    projectId: number;
}

// function getRandomInt(min, max) {
//     min = Math.ceil(min);
//     max = Math.floor(max);
//     return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
// }

// function generateRandomData() {
//     const arr: ProjectSpendingRow[] = [];
//     for (let i = 1; i <= 12; i++) {
//         arr.push({
//             rootItemCategoryId: i,
//             totalSpending: getRandomInt(10000, 500000),
//             averageSpending: getRandomInt(1000, 50000),
//         });
//     }

//     return arr;
// }

// const exampleSpendingData = generateRandomData();

const ProjectDashboard: React.FC<ProjectDashboardProps> = ({ projectId }) => {
    const categories = useAppSelector((s) => s.global.itemCategories);
    const getCategoryName = (itemCategoryId: number) => findCategoryName(categories, itemCategoryId);

    const [spending] = useAsyncState(() => getProjectDashboardSpending(projectId), [projectId], undefined);
    const [availability] = useAsyncState(() => getProjectDashboardAvailability(projectId), [projectId], undefined);
    const [timeToOffer] = useAsyncState(() => getProjectDashboardTimeToOffer(projectId), [projectId], undefined);
    const [deadlines] = useAsyncState(() => getProjectDashboardDeadlineOverview(projectId), [projectId], undefined);
    // const [projectDeadlines] =
    const [chartType, setChartType] = useState<'total' | 'average'>('total');

    const [changeRequestStatusOverview] = useAsyncState(
        () => getProjectChangeRequestStatusOverview(projectId),
        [projectId],
        undefined
    );
    const projectOverview = useSelector((state: IState) => state.seller.projectOverview);
    return (
        <DashboardCardGrid rows={3} columns={4} className={theme.container}>
            <DashboardDataCard
                infoIconContent={<Trans>This card indicates the number of unapproved objects outstanding.</Trans>}
                title={<Trans>Unapproved objects</Trans>}
                rowSpan={2}
                loading={!projectOverview}>
                {() => (
                    <div className={theme.unapprovedTasks}>
                        <div>
                            <ProjectRelativeLink to={`/project-documents?status=pending`} className={theme.tasksLink}>
                                {projectOverview.tasks?.projectDocuments}
                                <small>
                                    <Trans>Project documents</Trans>
                                </small>
                            </ProjectRelativeLink>
                        </div>
                        <div>
                            <ProjectRelativeLink to={`/documents?status=pending`} className={theme.tasksLink}>
                                {projectOverview.tasks?.documents}
                                <small>
                                    <Trans id="project_dashboard_residential_document">Documents</Trans>
                                </small>
                            </ProjectRelativeLink>
                        </div>
                        <div>
                            <ProjectRelativeLink to={`/options/tasks`} className={theme.tasksLink}>
                                {projectOverview.tasks?.optionGroups}
                                <small>
                                    <Trans>Options</Trans>
                                </small>
                            </ProjectRelativeLink>
                        </div>
                    </div>
                )}
            </DashboardDataCard>

            <DashboardDataCard
                contentAlignment="top"
                className={theme.deadlinesCard}
                title={<Trans>Units with sent options by deadline</Trans>}
                loading={!deadlines}>
                {() =>
                    deadlines!.length > 0 ? (
                        <div className={theme.dashboardTable}>
                            {deadlines!
                                .filter((x) => x.unitsWithOptionsTotal)
                                .map((d) => (
                                    <Fragment key={d.projectDeadlineId}>
                                        <div className={theme.deadlineName}>{d.projectDeadlineName}</div>
                                        <div className={theme.unitCount}>
                                            {d.unitsWithOptionsTotal === 0 ? (
                                                <small>
                                                    <i>
                                                        <Trans>N/A</Trans>
                                                    </i>
                                                </small>
                                            ) : (
                                                <>
                                                    <ProjectRelativeLink
                                                        to={`/residential-units/expanded?${getFilterQueryString<ResidentialUnitFilterOptions>(
                                                            {
                                                                hasUnsentOptions: 'yes',
                                                                hasDeadline: [d.projectDeadlineId],
                                                            }
                                                        )}`}>
                                                        {d.unitsWithOptionsSent}
                                                        <small>
                                                            /{d.unitsWithOptionsTotal} <Trans>units</Trans>
                                                        </small>
                                                    </ProjectRelativeLink>
                                                </>
                                            )}
                                        </div>
                                    </Fragment>
                                ))}
                        </div>
                    ) : (
                        <div className={theme.noDeadlinesMessage}>
                            <Trans>No deadlines</Trans>
                        </div>
                    )
                }
            </DashboardDataCard>

            {chartType === 'total' && (
                <DashboardDataCard
                    infoIconContent={<Trans>This card indicates the total spend of buyers per discipline.</Trans>}
                    title={<Trans id="Total spend per discipline(as seller)">Total spend per discipline</Trans>}
                    colSpan={2}
                    rowSpan={2}
                    loading={!spending}
                    onClick={(e) => setChartType('average')}>
                    {() => (
                        <SimpleBarChart<ProjectSpendingRow>
                            data={spending!.details.map((d) => ({
                                ...d,
                                totalSpending: +d.totalSpending.toFixed(0),
                            }))}
                            nameKey={(row) => getCategoryName(row.rootItemCategoryId) || <Trans>Uncategorized</Trans>}
                            valueKey={'totalSpending'}
                        />
                    )}
                </DashboardDataCard>
            )}

            {chartType === 'average' && (
                <DashboardDataCard
                    infoIconContent={<Trans>This card indicates the average spend of buyers per discipline.</Trans>}
                    title={<Trans>Average spend per discipline</Trans>}
                    colSpan={2}
                    rowSpan={2}
                    loading={!spending}
                    onClick={(e) => setChartType('total')}>
                    {() => (
                        <SimpleBarChart<ProjectSpendingRow>
                            data={spending!.details.map((d) => ({
                                ...d,
                                averageSpending: +d.averageSpending.toFixed(0),
                            }))}
                            nameKey={(row) => getCategoryName(row.rootItemCategoryId) || <Trans>Uncategorized</Trans>}
                            valueKey={'averageSpending'}
                        />
                    )}
                </DashboardDataCard>
            )}
            <DashboardDataCard
                infoIconContent={
                    <Trans>
                        This card indicates the average time taken to send an offer to a buyer from a change request.
                    </Trans>
                }
                title={<Trans>Average time to send offer</Trans>}
                loading={timeToOffer === undefined}>
                {() => (timeToOffer === null ? <Trans>N/A</Trans> : <Trans>{timeToOffer} days</Trans>)}
            </DashboardDataCard>

            <DashboardDataCard
                infoIconContent={<Trans>This card indicates the number of units sold.</Trans>}
                title={<Trans>Units</Trans>}
                loading={!availability}>
                {() => (
                    <div className={theme.unapprovedTasks}>
                        <div>
                            <ProjectRelativeLink
                                to={`/residential-units/compressed?${getFilterQueryString<ResidentialUnitFilterOptions>({
                                    soldStatus: 'sold',
                                })}`}
                                className={theme.tasksLink}>
                                {availability!.unitsSold}
                                <small>
                                    <Trans>Sold</Trans>
                                </small>
                            </ProjectRelativeLink>
                        </div>
                        <div>
                            <ProjectRelativeLink
                                to={`/residential-units/compressed?${getFilterQueryString<ResidentialUnitFilterOptions>({
                                    soldStatus: 'unsold',
                                })}`}
                                className={theme.tasksLink}>
                                {availability!.unitsTotal - availability!.unitsSold}
                                <small>
                                    <Trans id="Available.Plural">Available</Trans>
                                </small>
                            </ProjectRelativeLink>
                        </div>
                    </div>
                )}
            </DashboardDataCard>
            <DashboardDataCard
                infoIconContent={<Trans>This card indicates the total spend of buyers on options.</Trans>}
                title={<Trans id="Total spent on options(as seller)">Total spent on options</Trans>}
                loading={!spending}>
                {() => (
                    <div>
                        <ProjectCurrencyDisplay value={spending!.totalSpending} />
                        <small>
                            <Trans>
                                <ProjectCurrencyDisplay value={spending!.averageSpending} /> on average per sold unit
                            </Trans>
                        </small>
                    </div>
                )}
            </DashboardDataCard>

            <DashboardDataCard
                rowSpan={2}
                infoIconContent={
                    <Trans>This card indicates the number of outstanding change requests without offers.</Trans>
                }
                title={<Trans>Change requests</Trans>}
                loading={!changeRequestStatusOverview}
                contentAlignment="top">
                {() => (
                    <>
                        <div className={theme.dashboardTable}>
                            {changeRequestStatusOverview?.items.map((x) => (
                                <Fragment key={x.status}>
                                    <div className={theme.changeRequestStatus}>
                                        <ChangeRequestStatusIndicator value={SellerChangeRequestStatus[x.status]} />
                                    </div>
                                    <div className={theme.unitCount}>
                                        <ProjectRelativeLink
                                            to={`/changes?${getFilterQueryString<ChangeRequestFilterValue>({
                                                statuses: [x.status],
                                            })}`}>
                                            {x.count}{' '}
                                            <small>
                                                <Trans>requests</Trans>
                                            </small>
                                        </ProjectRelativeLink>
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                    </>
                )}
            </DashboardDataCard>
        </DashboardCardGrid>
    );
};

export default ProjectDashboard;
